import React, { useEffect, useState } from "react";
import Select from "react-select";
import ApiService from "../../Core/Service/ApiService";
import { useNavigate, useParams } from "react-router-dom";
import { LondonDate, capitalizeFirstLetter, getImageUrl, validateForm } from "../../Utils/utils";
import { toast } from "react-toastify";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import { MDBBtn, MDBCheckbox, MDBCol, MDBInput, MDBRadio, MDBRow, MDBSpinner } from "mdb-react-ui-kit";
import ImageUploader from "../../Components/ImageUploader/ImageUploader";
import { useContext } from "react";
import CmsContext from "../../Context/CmsContext";

const OrganizationEdit = React.memo(props => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        email: '',
        telephone: '',
        address: '',
        post_code: '',
        logo_web: {
            upload_file: '',
            external_link: '',
            active_link: 'external'
        },
        logo_mobile: {
            upload_file: '',
            external_link: '',
            active_link: 'external'
        },
    });
    const params = useParams();
    const navigate = useNavigate();
    let rules = {
        name: {
            required: 'string'
        },
        domain: {
            required: 'string'
        }
    }
    const [inputErrors, setInputErrors] = useState({});
    const cmsContext = useContext(CmsContext);

    useEffect(() => {
        cmsContext.setBreadcrumb([
            { title: cmsContext.currMainMenu?.label ?? ''},
            { title: cmsContext.currSubMenu?.label ?? ''},
            { title: capitalizeFirstLetter(params.action) },
        ])
    }, [])

    const loadData = async () => {
        setLoading(true);

        if(params.action == 'edit' || params.action == 'copy') {
            let response = await ApiService.get(`/organizations/show/${params.id}`);
            const organization = response.data.organization;
            delete organization.id;
            setFormData({
                ...organization,
                logo_web: organization.logo_web ? JSON.parse(organization.logo_web) : formData.logo_web,
                logo_mobile: organization.logo_mobile ? JSON.parse(organization.logo_mobile) : formData.logo_mobile,
            });
        }

        setLoading(false);
    }

    useEffect(() => {
        loadData()
    }, [params.action])

    useEffect(() => {
        const ret = validateForm(formData, rules);
        setInputErrors(ret.errors);
    }, [formData])

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        })
    }
    const handleSave = () => {
        const ret = validateForm(formData, rules);
        if(ret.result) {
            setInputErrors({});
            setSaving(true);

            const data = {
                ...formData,
                logo_web: JSON.stringify(formData.logo_web),
                logo_mobile: JSON.stringify(formData.logo_mobile),
            }

            const url = (params.action == 'edit') ? `/organizations/save/${params.id}` : '/organizations/create';
            ApiService.post(url, data).then(response => {
                navigate(-1);            
            })
            .finally(()=>{
                setSaving(false);
            })
    
        } else {
            setInputErrors(ret.errors)
        }
    }
    const onImageUploaded = async (id, upload_file) => {
        setFormData({
            ...formData,
            [id]: {
                ...formData[id],
                upload_file: upload_file
            }
        });
    }
    const onImageDeleted = async(id) => {
        setFormData({
            ...formData,
            [id]: {
                ...formData[id],
                upload_file: ''
            }
        })
    }
    const handleMediaLinkTypeChange = async (e, target, link_type) => {
        setFormData({
            ...formData,
            [target]: {
                ...formData[target],
                active_link: link_type
            }
        })
    }
    const handleMediaExternalLinkChange = async(e, target) => {
        setFormData({
            ...formData,
            [target]: {
                ...formData[target],
                external_link: e.target.value
            }
        })
    }

    return (
    <div className="organization-list mx-auto w-fit-content">
        <MDBRow style={{maxWidth: 1024}}>
            <MDBCol size={12} md={6} className="mt-4">
                <label className="required">Name:</label>
                <MDBInput name='name' value={formData.name ?? ''} onChange={handleInputChange} />
                {inputErrors?.name && <div className="error">{inputErrors.name}</div>}
            </MDBCol>
            <MDBCol size={12} md={6} className="mt-4">
                <label className="required">Domain:</label>
                <MDBInput name='domain' value={formData.domain ?? ''} onChange={handleInputChange} />
                {inputErrors?.domain && <div className="error">{inputErrors.domain}</div>}
            </MDBCol>
            <MDBCol className="mt-4" size={12} md={6}>
                <label>Email:</label>
                <MDBInput name='email' value={formData.email ?? ''} onChange={handleInputChange} />
            </MDBCol>
            <MDBCol className="mt-4" size={12} md={6}>
                <label>Telephone:</label>
                <MDBInput name='telephone' value={formData.telephone ?? ''} onChange={handleInputChange} />
            </MDBCol>
            <MDBCol className="mt-4" size={12} md={6}>
                <label>Post Code:</label>
                <MDBInput name='post_code' value={formData.post_code ?? ''} onChange={handleInputChange} />
            </MDBCol>
            <MDBCol className="mt-4" size={6}>
                <label>Address:</label>
                <MDBInput name='address' value={formData.address ?? ''} onChange={handleInputChange} />
            </MDBCol>
            <MDBCol className="mt-4" size={12}>
                <div className="d-flex align-items-center mt-4">
                    <div className="flex-fluid">
                        <label className="d-flex align-items-center">
                            <span className="me-4">Logo (Web):</span>
                        </label>
                        <div className="mt-2">
                            <div>
                                <MDBRadio 
                                    label='External Link' 
                                    checked={formData.logo_web?.active_link == 'external'} 
                                    onChange={(e)=>handleMediaLinkTypeChange(e, 'logo_web', 'external')}
                                />
                                <MDBInput 
                                    className={formData.logo_web?.active_link == 'external' ? '' : 'disabled'}
                                    value={formData.logo_web?.external_link} 
                                    onChange={e=>handleMediaExternalLinkChange(e, 'logo_web')} 
                                />
                            </div>
                            <div className="mt-2">
                                <MDBRadio 
                                    label='Upload Media' 
                                    checked={formData.logo_web?.active_link == 'upload'}
                                    onChange={(e)=>handleMediaLinkTypeChange(e, 'logo_web', 'upload')}
                                />
                                <ImageUploader 
                                    id='logo_web' 
                                    className={formData.logo_web?.active_link == 'upload' ? '' : 'disabled'}
                                    onImageUploaded={onImageUploaded} 
                                    onImageDeleted={onImageDeleted}
                                    defaultSrc={formData.logo_web?.upload_file} 
                                    folder="web" 
                                /> 
                            </div>
                        </div>
                    </div>
                    <div className="ms-4 flex-fluid">
                        <label className="d-flex align-items-center">
                            <span className="me-2">Media (Mobile):</span>
                        </label>
                        <div className="mt-2">
                            <div>
                                <MDBRadio
                                    label='External Link'
                                    checked={formData.logo_mobile?.active_link == 'external'}
                                    onChange={(e) => handleMediaLinkTypeChange(e,'logo_mobile', 'external')}
                                />
                                <MDBInput
                                    className={formData.logo_mobile?.active_link == 'external' ? '' : 'disabled'}
                                    value={formData.logo_mobile?.external_link}
                                    onChange={e=>handleMediaExternalLinkChange(e, 'logo_mobile')}
                                />
                            </div>
                            <div className="mt-2">
                                <MDBRadio
                                    label='Upload Media'
                                    checked={formData.logo_mobile?.active_link == 'upload'}
                                    onChange={e=>handleMediaLinkTypeChange(e, 'logo_mobile', 'upload')}
                                />
                                <ImageUploader 
                                    id='logo_mobile' 
                                    className={formData.logo_mobile.active_link == 'upload' ? '' :'disabled'}
                                    onImageUploaded={onImageUploaded} 
                                    onImageDeleted={onImageDeleted}
                                    defaultSrc={formData.logo_mobile?.upload_file} 
                                    folder="mobile" 
                                /> 
                            </div>
                        </div>
                    </div>
                </div>
            </MDBCol>
        </MDBRow>

        <div className="mt-4 d-flex justify-content-end">
            <MDBBtn color="default" onClick={e=>navigate(-1)}>Back</MDBBtn>
            <MDBBtn className="united-cms-primary-btn ms-4" onClick={handleSave} disabled={saving}>
                {saving && <MDBSpinner size="sm" className="me-1" />}
                Save
            </MDBBtn>
        </div>

        <LoadingIndicator show={loading} />
    </div>
    )
});

export default OrganizationEdit;
